/* QuickHazardCapture — 1-tap report flow.
   1. Apertura immediata fotocamera del telefono (input capture="environment")
   2. Foto scattata → tap di un tipo (chips grandi) → didascalia opzionale (auto-prefill)
   3. Submit veloce con geo automatica
   Mobile-first, fullscreen sheet.
*/
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, X, MapPin, ShieldAlert, Send, Loader2,
  Sparkles, RotateCcw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatErr } from "@/lib/api";
import { hapticTap, hapticSuccess, hapticEmergency } from "@/lib/haptics";

// Same enum as backend HAZARD_TYPES — short labels + emoji. Solo pericoli per cani.
const QUICK_TYPES = [
  { id: "poison_bait",   label: "Bocconi",        emoji: "☠️", color: "#FF3B30", placeholder: "Possibile boccone avvelenato per cani" },
  { id: "dangerous_dog", label: "Cane sciolto",   emoji: "🐕", color: "#FF3B30", placeholder: "Cane sciolto, sembra aggressivo" },
  { id: "glass",         label: "Vetri",          emoji: "🧊", color: "#FFB020", placeholder: "Vetri / oggetti taglienti sul percorso" },
  { id: "chemical",      label: "Sostanze",       emoji: "🧪", color: "#FF3B30", placeholder: "Antigelo / lumachicida / sostanza tossica" },
  { id: "wild_animal",   label: "Animale selv.",  emoji: "🦊", color: "#FFB020", placeholder: "Cinghiale / volpe / animale aggressivo" },
  { id: "other",         label: "Altro",          emoji: "⚠️", color: "#FFB020", placeholder: "Pericolo da segnalare ad altri padroni" },
];

export default function QuickHazardCapture({ open, onClose, onDone }) {
  const fileRef = useRef(null);
  const autoOpenedRef = useRef(false);
  const [step, setStep] = useState("camera"); // camera | review
  const [photo, setPhoto] = useState(null);
  const [type, setType] = useState(null);
  const [caption, setCaption] = useState("");
  const [loc, setLoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep("camera");
    setPhoto(null);
    setType(null);
    setCaption("");
    setLoc(null);
    autoOpenedRef.current = false;

    // Pre-fetch location ASAP (parallel to camera)
    if (navigator.geolocation) {
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (p) => { setLoc({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocating(false); },
        () => setLocating(false),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
      );
    }

    // Auto-open camera right away (only once)
    const t = setTimeout(() => {
      if (!autoOpenedRef.current && fileRef.current) {
        autoOpenedRef.current = true;
        try { fileRef.current.click(); } catch {/* ignore */}
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      // user cancelled camera → fall back to manual flow
      return;
    }
    if (f.size > 4 * 1024 * 1024) return toast.error("Foto troppo grande (max 4MB)");
    const r = new FileReader();
    r.onload = () => {
      setPhoto(r.result);
      setStep("review");
      hapticTap();
    };
    r.readAsDataURL(f);
  };

  const pickType = (t) => {
    hapticTap();
    setType(t.id);
    // Pre-fill the caption with a friendly placeholder the user can tweak
    if (!caption) setCaption(t.placeholder);
  };

  const submit = async () => {
    if (!type) return toast.error("Tocca un tipo di pericolo");
    if (!loc) return toast.error("Posizione non ancora disponibile, attendi");
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/hazards", {
        type,
        description: caption.trim(),
        lat: loc.lat,
        lng: loc.lng,
        photo_url: photo,
      });
      hapticSuccess();
      toast.success(`Pericolo segnalato · avvisati ${data.notified || 0} utenti vicini · +3 karma`);
      try {
        window.dispatchEvent(new CustomEvent("orio:karma", { detail: { delta: 3, reason: "hazard_create" } }));
        window.dispatchEvent(new CustomEvent("orio:hazards-changed"));
      } catch {/* ignore */}
      onDone?.(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  const selected = QUICK_TYPES.find((t) => t.id === type);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1700] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0"
        data-testid="quick-hazard-capture"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          className="relative w-full sm:max-w-md mx-0 sm:mx-3 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden flex flex-col"
          style={{ maxHeight: "min(86dvh, calc(100dvh - 170px))", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.40)" }}
        >
          {/* Hidden file input — used to open native camera */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="quick-hazard-file-input"
          />

          {/* Header */}
          <div className="relative px-5 pt-5 pb-3 border-b border-[#F0EBE3]">
            <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full opacity-50 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,176,32,0.30) 0%, transparent 60%)" }} />
            <div className="absolute -top-8 -left-12 h-28 w-28 rounded-full opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,59,48,0.30) 0%, transparent 60%)" }} />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFB020] inline-flex items-center gap-1.5">
                  <ShieldAlert className="h-3 w-3" /> Segnalazione rapida
                </div>
                <h2 className="mt-1 font-display text-2xl text-[#1A1A1A] leading-tight">
                  {step === "camera" ? "Scatta una foto" : "Conferma e invia"}
                </h2>
                <div className="mt-0.5 text-[11px] text-[#6B6B6B]">
                  {step === "camera"
                    ? "Apri la fotocamera · 1 scatto basta"
                    : "Tappa il tipo · invia in 3 secondi"}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="quick-hazard-close"
                aria-label="Chiudi"
                className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center"
              >
                <X className="h-4 w-4 text-[#6B6B6B]" />
              </button>
            </div>
          </div>

          {/* ───── CAMERA STEP ───── */}
          {step === "camera" && (
            <div className="px-5 py-8 text-center" data-testid="quick-hazard-step-camera">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mx-auto h-24 w-24 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #FFB020 0%, #FF3B30 100%)",
                  boxShadow: "0 16px 36px -10px rgba(255,176,32,0.55)",
                }}
              >
                <Camera className="h-12 w-12 text-white" />
              </motion.div>
              <p className="mt-5 text-[14px] text-[#1A1A1A] leading-relaxed max-w-xs mx-auto">
                Si è aperta la fotocamera del telefono? Scatta la foto del pericolo, poi torni qui.
              </p>
              <p className="mt-2 text-[11px] text-[#9A9A9A]">
                Se non si apre, premi il pulsante qui sotto.
              </p>
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={() => { hapticTap(); fileRef.current?.click(); }}
                data-testid="quick-hazard-open-camera"
                className="mt-5 px-5 py-3 rounded-full text-white font-semibold text-[14px] inline-flex items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #FFB020 0%, #FF3B30 100%)",
                  boxShadow: "0 14px 30px -10px rgba(255,176,32,0.55)",
                }}
              >
                <Camera className="h-4 w-4" /> Apri fotocamera
              </motion.button>
              {locating && (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#9A9A9A]">
                  <Loader2 className="h-3 w-3 animate-spin" /> Trovo la posizione…
                </div>
              )}
              {loc && !locating && (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#2DBA60]">
                  <MapPin className="h-3 w-3" /> Posizione pronta
                </div>
              )}
            </div>
          )}

          {/* ───── REVIEW STEP ───── */}
          {step === "review" && (
            <div className="overflow-y-auto" data-testid="quick-hazard-step-review">
              {/* Photo */}
              <div className="relative w-full" style={{ aspectRatio: "4/3", background: "#1A1A1A" }}>
                <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <button
                  onClick={() => { setPhoto(null); setStep("camera"); fileRef.current?.click(); }}
                  data-testid="quick-hazard-retake"
                  className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full text-[12px] text-white bg-black/55 backdrop-blur inline-flex items-center gap-1.5 hover:bg-black/70"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Rifai
                </button>
                {loc && (
                  <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-[10px] text-white bg-black/55 backdrop-blur inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Posizione rilevata
                  </span>
                )}
              </div>

              {/* Type chips — big & tappable */}
              <div className="px-5 pt-4">
                <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A] font-semibold mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3 text-[#FFB020]" /> Che cos'è?
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="quick-hazard-types">
                  {QUICK_TYPES.map((t) => {
                    const active = type === t.id;
                    return (
                      <motion.button
                        key={t.id}
                        whileTap={{ scale: 0.94 }}
                        data-testid={`quick-hz-type-${t.id}`}
                        onClick={() => pickType(t)}
                        className="relative rounded-2xl p-3 border-2 text-left transition-all flex flex-col items-center gap-1"
                        style={{
                          background: active ? `${t.color}1F` : "white",
                          borderColor: active ? t.color : "#F0EBE3",
                        }}
                      >
                        <span className="text-2xl leading-none">{t.emoji}</span>
                        <span className="text-[11px] font-semibold leading-tight text-center"
                          style={{ color: active ? t.color : "#1A1A1A" }}>{t.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Caption */}
              <div className="px-5 pt-4">
                <label className="text-[10px] uppercase tracking-widest text-[#9A9A9A] font-semibold">
                  Didascalia (opzionale)
                </label>
                <input
                  data-testid="quick-hazard-caption"
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder={selected?.placeholder || "Es. Vetri sul marciapiede vicino al parco"}
                  maxLength={120}
                  className="mt-1 w-full px-3.5 py-2.5 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] text-[14px] text-[#1A1A1A] outline-none focus:border-[#FFB020]/50 focus:bg-white"
                />
                <div className="mt-1 text-right text-[10px] text-[#9A9A9A]">{caption.length}/120</div>
              </div>

              {/* Submit */}
              <div className="px-5 pb-5 pt-2 sticky bottom-0 bg-white">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={submit}
                  disabled={!type || !loc || submitting}
                  data-testid="quick-hazard-submit"
                  className="w-full py-3.5 rounded-full text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-55"
                  style={{
                    background: type
                      ? `linear-gradient(135deg, ${selected?.color || "#FFB020"} 0%, #FF3B30 100%)`
                      : "linear-gradient(135deg, #C7C0B3 0%, #9A9A9A 100%)",
                    boxShadow: type ? "0 14px 30px -10px rgba(255,59,48,0.45)" : "none",
                  }}
                >
                  {submitting
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Pubblico…</>
                    : !type
                      ? "Scegli un tipo"
                      : !loc
                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Attendo posizione…</>
                        : <><Send className="h-4 w-4" /> Pubblica · avvisa cani vicini</>}
                </motion.button>
                <div className="mt-2 text-center text-[10.5px] text-[#9A9A9A]">
                  +3 karma · invia push a chi è entro 1 km
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
